import moment from "moment";
import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";

export const maxTimetableNotesLength = 100;

export interface TimetableEntryFormState {
    startTime: moment.Moment | undefined;
    endTime: moment.Moment | undefined;
    dayOffset: number;
    type: server.dto.TimetableEntryType;
    customText: string;
    notes: string;
}

export const timetableDayOffsets = [0, 1];

export const formatDayOffset = (dayOffset: number, dateOfEventLabel: string): string => {
    if (dayOffset === 0) return dateOfEventLabel;

    return `${dateOfEventLabel} ${dayOffset > 0 ? "+" : "-"}${Math.abs(dayOffset)}`;
};

export interface TimetableEntryFieldMetaData {
    startTime: FieldMetaData;
    endTime: FieldMetaData;
    dayOffset: FieldMetaData;
    notes: FieldMetaData;
    type: FieldMetaData;
}

export const getTimetableEntryFieldMetaData = (
    item: TimetableEntryFormState,
    canModifyTimetable: boolean
): TimetableEntryFieldMetaData => ({
    startTime: createFieldMetaData(
        !item.startTime,
        !item.startTime && !canModifyTimetable, //Hide when empty and read-only
        !canModifyTimetable,
    ),
    endTime: createFieldMetaData(
        !item.endTime,
        !item.endTime && !canModifyTimetable,
        !canModifyTimetable,
    ),
    dayOffset: createFieldMetaData(
        false,
        false,
        !canModifyTimetable,
    ),
    notes: createFieldMetaData(
        !item.notes,
        !item.notes && !canModifyTimetable,
        !canModifyTimetable,
    ),
    type: createFieldMetaData(
        false,
        false,
        !canModifyTimetable,
    ),
});

export const getInitialTimetableEntryFormState = (): TimetableEntryFormState => ({
    startTime: undefined,
    endTime: undefined,
    dayOffset: 0,
    type: { value: "custom", text: "" },
    customText: "",
    notes: "",
});

export const mapTimetableEntryToFormState = (entry: server.dto.TimetableEntry): TimetableEntryFormState => ({
    startTime: entry.startTime ? moment.utc(entry.startTime) : undefined,
    endTime: entry.endTime ? moment.utc(entry.endTime) : undefined,
    dayOffset: entry.nextDay ? 1 : 0,
    type: entry.type,
    customText: entry.type.value === "custom" ? entry.type.text : "",
    notes: entry.notes ?? "",
});

export const formatTimetableEntryType = (type: server.dto.TimetableEntryType, customText: string): string =>
    type.value === "custom" ? customText : type.text;

const getCustomText = (state: TimetableEntryFormState) =>
    state.type.value === "custom" ? state.customText : undefined;

export const getCreateTimetableEntryInstruction = (
    showId: number,
    state: TimetableEntryFormState,
): server.dto.CreateTimetableEntryInstruction => ({
    showId,
    startTime: state.startTime?.toDate(),
    endTime: state.endTime?.toDate(),
    nextDay: state.dayOffset > 0,
    type: state.type.value,
    customText: getCustomText(state),
    notes: state.notes,
});

export const getUpdateTimetableEntryInstruction = (
    timetableEntryId: number,
    state: TimetableEntryFormState,
): server.dto.ModifyTimetableEntryInstruction => ({
    timetableEntryId,
    startTime: state.startTime?.toDate(),
    endTime: state.endTime?.toDate(),
    nextDay: state.dayOffset > 0,
    type: state.type.value,
    customText: getCustomText(state),
    notes: state.notes,
});
